import { useContext, useEffect, useState } from "react";
import { EditorContext } from "../containers/editor/EditorLayout";
import {
  ANIMATION,
  GIF,
  ICON,
  ILLUSTRATION,
  LINE,
  PICTURE,
  QR_CODE,
  SHAPE,
  STICKER,
  STOCK,
  SVG,
  TABLE,
  TEXT,
  TEXT_FRAME,
  TYPE_FORM,
  JOT_FORM,
  UPLOAD,
  VIDEO,
  YOUTUBE,
  COLLAGE,
  MAP,
  BRAND,
  UPLOAD_VIDEO,
  PRICING_TABLE,
  FRAME,
  TEXT_ART,
  TIMELINE,
  SCOPE_OF_WORK,
  ITINERARY,
  PORTFOLIO,
  CASESTUDY,
} from "../constants/editor";
import { RGBToHex, changeBucketColorAccordingBackground, getRandomString, hexToRGB } from "../_helpers/utils";
import { EditorPanelContext } from "../containers/editor/EditorPanel";

const useCopyStyleWidget = () => {
  let { widgets, updateWidgets } = useContext(EditorContext);
  let { copyStyle, updateCopyStyle } = useContext(EditorPanelContext);

  const [copyStyleWidget, setCopyStyleWidget] = useState(false);

  const getNode = currentActiveWidgetId => {
    let node;
    if (document.querySelector(`#${currentActiveWidgetId} svg rect`)) {
      node = document.querySelector(`#${currentActiveWidgetId} svg rect`);
    } else if (document.querySelector(`#${currentActiveWidgetId} svg circle`)) {
      node = document.querySelector(`#${currentActiveWidgetId} svg circle`);
    } else if (document.querySelector(`#${currentActiveWidgetId} svg polygon`)) {
      node = document.querySelector(`#${currentActiveWidgetId} svg polygon`);
    } else if (document.querySelector(`#${currentActiveWidgetId} svg ellipse`)) {
      node = document.querySelector(`#${currentActiveWidgetId} svg ellipse`);
    } else if (document.querySelector(`#${currentActiveWidgetId} svg path`)) {
      node = document.querySelector(`#${currentActiveWidgetId} svg path`);
    }
    return node;
  };

  useEffect(() => {
    if (copyStyleWidget) {
      let currentActiveWidget = copyStyleWidget;
      let currentActiveWidgetId = currentActiveWidget.current.id;
      let currentActiveWidgetIdx = widgets.findIndex(widget => widget.id === currentActiveWidgetId);
      let currentActiveElem = document.getElementById(currentActiveWidgetId);
      let currentActiveInnerElem = currentActiveElem.querySelector(" .dhp-widget-inner");
      let cropped = currentActiveInnerElem.style.clipPath === "" ? false : true;
      let transparentImage = currentActiveElem.getElementsByTagName("img")[0]?.src.includes("transparent-bg");
      let borderInculdedWidgetType = [TEXT, PICTURE, VIDEO, UPLOAD, TYPE_FORM, JOT_FORM, QR_CODE, SHAPE, FRAME];
      let colorExcludedWidgetType = [PICTURE, VIDEO, ANIMATION, TABLE, TYPE_FORM, JOT_FORM, MAP];
      let filterInculdedWidgetType = [PICTURE, VIDEO, ANIMATION, COLLAGE];
      let changeLineThickness = false;
      let changeHeading = false;
      let removeGradColor = false;
      let changeList = false;
      let newDataAttr = {};
      let headingValue, node, newArray;

      document.getElementById("canvas-panel-area").classList.remove("cursor-copy-style");

      if (currentActiveWidget.current.id === copyStyle.id) return;

      // NOTE:::: Once copy-style is applied ~ below code restores widget cursor style
      [...document.querySelectorAll(".dhp-page-widget")].forEach(widget => {
        widget.classList.remove("cursor-copy-style");
      });

      // Copy style for same type widget
      if (
        currentActiveWidget.current.dataset.assetType === copyStyle.widgetType &&
        copyStyle.widgetType !== VIDEO &&
        copyStyle.widgetType !== UPLOAD &&
        copyStyle.widgetType !== PICTURE &&
        copyStyle.widgetType !== ANIMATION &&
        copyStyle.widgetType !== TYPE_FORM &&
        copyStyle.widgetType !== JOT_FORM &&
        copyStyle.widgetType !== SHAPE &&
        copyStyle.widgetType !== COLLAGE &&
        copyStyle.widgetType !== FRAME &&
        copyStyle.widgetType !== TEXT_ART
      ) {
        // Copy line thickness if both line widget thickness value is different
        if (
          copyStyle.widgetType === LINE &&
          copyStyle.dataset.linethickness !== currentActiveInnerElem.dataset.linethickness
        ) {
          changeLineThickness = parseInt(copyStyle.dataset.linethickness);
        }

        // Copy heading if both text widget heading value is different
        if (copyStyle.widgetType === TEXT && copyStyle.dataset["data-heading"] !== currentActiveElem.dataset.heading) {
          changeHeading = true;
          headingValue = copyStyle.dataset["data-heading"];
          let dataAttribute = currentActiveInnerElem.dataset;

          node = document.createElement(headingValue === "normal" ? "div" : headingValue);
          node.innerHTML = currentActiveInnerElem.innerHTML;
          node.classList = currentActiveInnerElem.classList;
          node.style.cssText = copyStyle.cssText;
          if (dataAttribute) {
            for (const [key, value] of Object.entries(dataAttribute)) {
              node.setAttribute(`data-${key}`, value);
            }
          }

          // remove formatting from all child div and apply copy style formatting
          node.querySelectorAll("*").forEach(elm => {
            if (elm.style?.textDecoration) {
              elm.style.textDecoration =
                copyStyle.style.textDecoration === "line-through" || !copyStyle.style.textDecoration
                  ? "none"
                  : copyStyle.style.textDecoration;
            }

            elm.style.fontWeight = copyStyle.style.fontWeight;
            elm.style.fontStyle = copyStyle.style.fontStyle;
          });
        }

        // remove formatting from all child div and apply copy style formatting
        if (copyStyle.widgetType === TEXT) {
          currentActiveInnerElem.querySelectorAll("*").forEach(node => {
            if (node.style?.textDecoration) {
              node.style.textDecoration =
                copyStyle.style.textDecoration === "line-through" || !copyStyle.style.textDecoration
                  ? "none"
                  : copyStyle.style.textDecoration;
            }

            node.style.fontWeight = copyStyle.style.fontWeight;
            node.style.fontStyle = copyStyle.style.fontStyle;
          });
        }

        // Copy list
        if (
          copyStyle.widgetType === TEXT &&
          (copyStyle.dataset["data-subwidget"] || currentActiveElem.dataset.subwidget)
        ) {
          let listValue;

          // Remove list from target widget as source widget has no list value
          if (!copyStyle.dataset["data-subwidget"]) {
            listValue = currentActiveElem.dataset.subwidget;
          }
          // Apply list from source widget
          else {
            if (copyStyle.dataset["data-subwidget"] === currentActiveElem.dataset.subwidget) {
              // If both source and target widget list value is same
              listValue = false;
            } else {
              listValue = copyStyle.dataset["data-subwidget"];
            }
          }
          changeList = listValue;
        }

        // Manaage transform property for widget inner div
        let transformProperty = currentActiveInnerElem.style.transform;
        let widgetInnerWidth = currentActiveInnerElem.style.width;
        let widgetInnerHeight = currentActiveInnerElem.style.height;

        currentActiveInnerElem.style.cssText = copyStyle.cssText;
        currentActiveInnerElem.style.transform = transformProperty;

        if (copyStyle.widgetType === TABLE) {
          currentActiveInnerElem.style.width = widgetInnerWidth;
          currentActiveInnerElem.style.height = widgetInnerHeight;
        }
        if ([SCOPE_OF_WORK, ITINERARY, PORTFOLIO, CASESTUDY].includes(copyStyle.widgetType)) {
          currentActiveInnerElem.style.width = widgetInnerWidth;
          currentActiveInnerElem.style.height = widgetInnerHeight;
          currentActiveInnerElem.style["transform-origin"] = "left top";
          currentActiveInnerElem.style.filter = copyStyle.style?.shadow !== "" ? copyStyle.style?.shadow : "none";
        }
        // -----------------------end Manaage transform property for widget inner div--------------------

        // color copy itinerary to itinerary
        if ([ITINERARY].includes(copyStyle.widgetType)) {
          // set header color
          let backgroundColorRangeHeader = changeBucketColorAccordingBackground(
            hexToRGB(copyStyle.style.sectionHeader)
          );
          currentActiveInnerElem.querySelectorAll(".pricing-table-section .card-header").forEach(element => {
            element.style.backgroundColor = copyStyle.style.sectionHeader; // set header color

            // set header content color based on background color range
            element.querySelectorAll("*").forEach(child => {
              child.style.color = backgroundColorRangeHeader === "dark" ? "#ffffff" : "#000000";

              if (child.classList.contains("icon-ui-calendar")) {
                if (backgroundColorRangeHeader === "dark") {
                  child.classList.remove("color-black");
                  child.classList.add("color-white");
                } else {
                  child.classList.remove("color-white");
                  child.classList.add("color-black");
                }
              }
            });
          });

          // set sub header color
          let backgroundColorRangeSubheader = changeBucketColorAccordingBackground(
            hexToRGB(copyStyle.style.sectionSubheader)
          );
          currentActiveInnerElem.querySelectorAll(".item-label").forEach(element => {
            element.style.backgroundColor = copyStyle.style.sectionSubheader; // set sub-header color

            // set sub-header content color based on background color range
            element.querySelector("ul").childNodes.forEach(child => {
              child.style.color = backgroundColorRangeSubheader === "dark" ? "#ffffff" : "#000000";
            });
          });

          // set body color
          let backgroundColorRangeBody = changeBucketColorAccordingBackground(hexToRGB(copyStyle.style.sectionBody));
          currentActiveInnerElem.querySelectorAll(".item-details").forEach(element => {
            element.style.backgroundColor = copyStyle.style.sectionBody; // set section body color
            element.style.color = backgroundColorRangeBody === "dark" ? "#ffffff" : "#000000"; // set section body content color based on background color range
          });

          // set border color
          currentActiveInnerElem.querySelectorAll(".pricing-table-section").forEach(element => {
            element.style.border = `1px solid ${copyStyle.style.sectionBorder}`;
          });
          currentActiveInnerElem.querySelectorAll(".pricing-table-section .card-header").forEach(element => {
            element.style.borderBottom = `1px solid ${copyStyle.style.sectionBorder}`;
          });
          currentActiveInnerElem.querySelectorAll(".pricing-table-section .card-body .item-label").forEach(element => {
            element.style.borderBottom = `1px solid ${copyStyle.style.sectionBorder}`;
          });
          currentActiveInnerElem
            .querySelectorAll(".pricing-table-section .card-body .item-details")
            .forEach(element => {
              element.style.borderBottom = `1px solid ${copyStyle.style.sectionBorder}`;
            });

          // prepare data attributes
          let tableConfig = currentActiveElem.getAttribute("data-table-config");
          tableConfig = JSON.parse(tableConfig);
          let colorConfig = {
            sectionHeaderColor: copyStyle.style.sectionHeader,
            sectionHeaderContentColor:
              changeBucketColorAccordingBackground(hexToRGB(copyStyle.style.sectionHeader)) === "dark"
                ? "#ffffff"
                : "#000000",
            sectionSubheaderColor: copyStyle.style.sectionSubheader,
            sectionSubheaderContentColor:
              changeBucketColorAccordingBackground(hexToRGB(copyStyle.style.sectionSubheader)) === "dark"
                ? "#ffffff"
                : "#000000",
            sectionBodyColor: copyStyle.style.sectionBody,
            sectionBodyContentColor:
              changeBucketColorAccordingBackground(hexToRGB(copyStyle.style.sectionBody)) === "dark"
                ? "#ffffff"
                : "#000000",
            sectionBorderColor: copyStyle.style.sectionBorder,
          };
          let newTableConfig = { ...tableConfig, ...colorConfig };
          let jsonString = JSON.stringify(newTableConfig);

          currentActiveElem.setAttribute("data-table-config", jsonString);

          // update new table config in data attr
          newDataAttr = {
            ...newDataAttr,
            "data-table-config": jsonString,
          };
        }

        // color copy scope of work to scope of work
        if ([SCOPE_OF_WORK].includes(copyStyle.widgetType)) {
          // set header color
          let backgroundColorRangeHeader = changeBucketColorAccordingBackground(
            hexToRGB(copyStyle.style.sectionHeader)
          );
          currentActiveInnerElem.querySelectorAll(".scope-of-work-section .card-header").forEach(element => {
            element.style.backgroundColor = copyStyle.style.sectionHeader; // set header color

            // set header content color based on background color range
            element.querySelectorAll("*").forEach(child => {
              child.style.color = backgroundColorRangeHeader === "dark" ? "#ffffff" : "#000000";

              if (child.classList.contains("icon-ui-calendar")) {
                if (backgroundColorRangeHeader === "dark") {
                  child.classList.remove("color-black");
                  child.classList.add("color-white");
                } else {
                  child.classList.remove("color-white");
                  child.classList.add("color-black");
                }
              }
            });
          });

          // set sub header color
          let backgroundColorRangeSubheader = changeBucketColorAccordingBackground(
            hexToRGB(copyStyle.style.sectionSubheader)
          );
          currentActiveInnerElem.querySelectorAll(".item-label").forEach(element => {
            element.style.backgroundColor = copyStyle.style.sectionSubheader; // set sub-header color

            // set sub-header content color based on background color range
            element.querySelector("ul").childNodes.forEach(child => {
              child.style.color = backgroundColorRangeSubheader === "dark" ? "#ffffff" : "#000000";
            });
          });

          // set body color
          let backgroundColorRangeBody = changeBucketColorAccordingBackground(hexToRGB(copyStyle.style.sectionBody));
          currentActiveInnerElem.querySelectorAll(".item-details").forEach(element => {
            element.style.backgroundColor = copyStyle.style.sectionBody; // set section body color
            element.style.color = backgroundColorRangeBody === "dark" ? "#ffffff" : "#000000"; // set section body content color based on background color range
          });

          // set border color
          currentActiveInnerElem.querySelectorAll(".scope-of-work-section").forEach(element => {
            element.style.border = `1px solid ${copyStyle.style.sectionBorder}`;
          });
          currentActiveInnerElem.querySelectorAll(".scope-of-work-section .card-header").forEach(element => {
            element.style.borderBottom = `1px solid ${copyStyle.style.sectionBorder}`;
          });
          currentActiveInnerElem.querySelectorAll(".scope-of-work-section .card-body .item-label").forEach(element => {
            element.style.borderBottom = `1px solid ${copyStyle.style.sectionBorder}`;
          });
          currentActiveInnerElem
            .querySelectorAll(".scope-of-work-section .card-body .item-details")
            .forEach(element => {
              element.style.borderBottom = `1px solid ${copyStyle.style.sectionBorder}`;
            });

          // prepare data attributes
          let tableConfig = currentActiveElem.getAttribute("data-table-config");
          tableConfig = JSON.parse(tableConfig);
          let colorConfig = {
            sectionHeaderColor: copyStyle.style.sectionHeader,
            sectionHeaderContentColor:
              changeBucketColorAccordingBackground(hexToRGB(copyStyle.style.sectionHeader)) === "dark"
                ? "#ffffff"
                : "#000000",
            sectionSubheaderColor: copyStyle.style.sectionSubheader,
            sectionSubheaderContentColor:
              changeBucketColorAccordingBackground(hexToRGB(copyStyle.style.sectionSubheader)) === "dark"
                ? "#ffffff"
                : "#000000",
            sectionBodyColor: copyStyle.style.sectionBody,
            sectionBodyContentColor:
              changeBucketColorAccordingBackground(hexToRGB(copyStyle.style.sectionBody)) === "dark"
                ? "#ffffff"
                : "#000000",
            sectionBorderColor: copyStyle.style.sectionBorder,
          };
          let newTableConfig = { ...tableConfig, ...colorConfig };
          let jsonString = JSON.stringify(newTableConfig);

          currentActiveElem.setAttribute("data-table-config", jsonString);

          // update new table config in data attr
          newDataAttr = {
            ...newDataAttr,
            "data-table-config": jsonString,
          };
        }

        // color copy portfolio to portfolio and case study
        if ([PORTFOLIO, CASESTUDY].includes(copyStyle.widgetType)) {
          //set tag color
          if (
            currentActiveInnerElem.querySelectorAll(".portfolio_editor .tags li") &&
            copyStyle.style.tagColor !== ""
          ) {
            currentActiveInnerElem.querySelectorAll(".portfolio_editor .tags li").forEach(element => {
              element.style.backgroundColor = copyStyle.style.tagColor;
              element.style.color =
                changeBucketColorAccordingBackground(hexToRGB(copyStyle.style.tagColor)) === "dark"
                  ? "#ffffff"
                  : "#000000";
            });
          }

          // set frame color
          currentActiveInnerElem.querySelectorAll(".portfolio_editor .card .left").forEach(element => {
            element.style.backgroundColor = copyStyle.style.frameColor;

            if (copyStyle.widgetType === CASESTUDY) {
              element.querySelectorAll("p").forEach(el => {
                el.style.color =
                  changeBucketColorAccordingBackground(hexToRGB(copyStyle.style.frameColor)) === "dark"
                    ? "#ffffff"
                    : "#000000";
              });
            }
          });

          // set body color
          let backgroundColorRangeBody = changeBucketColorAccordingBackground(hexToRGB(copyStyle.style.sectionBody));
          currentActiveInnerElem.querySelectorAll(".section-body").forEach(element => {
            let contentColor = backgroundColorRangeBody === "dark" ? "#ffffff" : "#000000";

            element.style.backgroundColor = copyStyle.style.sectionBody;
            element.querySelector("h5").style.color = contentColor;
            element.querySelectorAll(".card-text").forEach(el => {
              el.style.color = contentColor;
            });
          });

          // set border color
          currentActiveInnerElem.querySelectorAll(".card").forEach(element => {
            element.style.border = `1px solid ${copyStyle.style.sectionBorder}`;
          });

          // prepare data attributes
          let tableConfig = currentActiveElem.getAttribute("data-table-config");
          tableConfig = JSON.parse(tableConfig);
          let colorConfig = {
            frameColor: copyStyle.style.frameColor,
            frameContentColor:
              changeBucketColorAccordingBackground(hexToRGB(copyStyle.style.frameColor)) === "dark"
                ? "#ffffff"
                : "#000000",
            sectionBodyColor: copyStyle.style.sectionBody,
            sectionBodyContentColor:
              changeBucketColorAccordingBackground(hexToRGB(copyStyle.style.sectionBody)) === "dark"
                ? "#ffffff"
                : "#000000",
            sectionBorderColor: copyStyle.style.sectionBorder,
          };
          if (
            currentActiveInnerElem.querySelectorAll(".portfolio_editor .tags li") &&
            copyStyle.style.tagColor !== ""
          ) {
            colorConfig = {
              ...colorConfig,
              tagColor: copyStyle.style?.tagColor,
              tagContentColor:
                changeBucketColorAccordingBackground(hexToRGB(copyStyle.style.tagColor)) === "dark"
                  ? "#ffffff"
                  : "#000000",
            };
          }
          let newTableConfig = { ...tableConfig, ...colorConfig };
          let jsonString = JSON.stringify(newTableConfig);

          currentActiveElem.setAttribute("data-table-config", jsonString);

          // update new table config in data attr
          newDataAttr = {
            ...newDataAttr,
            "data-table-config": jsonString,
          };
        }

        // set data attributre for flip, for all widget
        // currentActiveInnerElem.setAttribute("data-horizontal", copyStyle.dataset.horizontal ?? 1);
        // currentActiveInnerElem.setAttribute("data-vertical", copyStyle.dataset.vertical ?? 1);
      }
      // Copy style for The widgets which are not same type, but has border and shadow property common
      else {
        //Copy shadow property
        let sourceWidgetShadowProperty, sourceWidgetImageFilterProperty;
        let sourceWidgetFlipableAvailable =
          copyStyle.widgetType === PICTURE ||
          copyStyle.widgetType === ANIMATION ||
          (copyStyle.widgetType === VIDEO && [STOCK, BRAND, UPLOAD_VIDEO].includes(copyStyle.dataset["data-source"])) ||
          (copyStyle.widgetType === UPLOAD && copyStyle.dataset["data-file-type"] !== SVG);
        let TargetWidgetFlipAvailable =
          currentActiveWidget.current.dataset.assetType === PICTURE ||
          currentActiveWidget.current.dataset.assetType === ANIMATION ||
          (currentActiveWidget.current.dataset.assetType === VIDEO &&
            [STOCK, BRAND, UPLOAD_VIDEO].includes(currentActiveWidget.current.dataset.source)) ||
          (currentActiveWidget.current.dataset.assetType === UPLOAD &&
            currentActiveWidget.current.dataset.fileType !== SVG);

        // ********** Fetch source and target widgets filter and shadow values ********* //
        // Fetch shadow, flip and filter value  If source widget has flippable class
        if (sourceWidgetFlipableAvailable) {
          sourceWidgetShadowProperty = copyStyle.style?.shadow !== "" ? copyStyle.style?.shadow : "none";
          sourceWidgetImageFilterProperty = copyStyle.style?.filter !== "" ? copyStyle.style?.filter : "none";
        } else if (copyStyle.widgetType === TYPE_FORM || copyStyle.widgetType === JOT_FORM) {
          sourceWidgetShadowProperty = copyStyle.style?.shadow !== "" ? copyStyle.style?.shadow : "none";
        } else if (
          (copyStyle.widgetType === SHAPE &&
            ["Circles", "Triangles", "Basic"].includes(copyStyle.dataset["data-category"])) ||
          (copyStyle.widgetType === SHAPE &&
            ["Rectangles"].includes(copyStyle.dataset["data-category"]) &&
            copyStyle.dataset["data-version"] < 3)
        ) {
          sourceWidgetShadowProperty = copyStyle.style?.shadow !== "" ? copyStyle.style?.shadow : "none";
        } else if (copyStyle.widgetType === COLLAGE) {
          sourceWidgetShadowProperty = copyStyle.style?.shadow !== "" ? copyStyle.style?.shadow : "none";
          sourceWidgetImageFilterProperty = copyStyle.style?.filter !== "" ? copyStyle.style?.filter : "none";
        } else if (
          [PRICING_TABLE, TIMELINE, SCOPE_OF_WORK, ITINERARY, PORTFOLIO, CASESTUDY].includes(copyStyle.widgetType)
        ) {
          sourceWidgetShadowProperty = copyStyle.style?.shadow !== "" ? copyStyle.style?.shadow : "none";
        } else if (copyStyle.widgetType === FRAME) {
          sourceWidgetShadowProperty = copyStyle.style?.shadow !== "" ? copyStyle.style?.shadow : "none";
        }
        // Fetch shadow value  If source widget has no flippable class
        else {
          sourceWidgetShadowProperty = copyStyle.style.filter ? copyStyle.style.filter : "none";
        }
        // ********** End Fetch section ********* //

        if (!TargetWidgetFlipAvailable && currentActiveWidget.current.dataset.assetType !== COLLAGE) {
          currentActiveInnerElem.style.filter = sourceWidgetShadowProperty;
        } else if (currentActiveWidget.current.dataset.assetType === COLLAGE) {
          currentActiveInnerElem.style.filter = sourceWidgetShadowProperty;
          currentActiveInnerElem.querySelector(".collage").style.filter = sourceWidgetImageFilterProperty;

          let telem = currentActiveInnerElem.querySelector(".collage");

          // remove or update data filter attribitues if only the source widget also have filter property
          if (
            filterInculdedWidgetType.includes(copyStyle.dataset["data-asset-type"]) ||
            (copyStyle.dataset["data-asset-type"] === UPLOAD && copyStyle.dataset["data-file-type"] !== SVG)
          ) {
            if (!copyStyle.dataset.filterType) {
              telem.removeAttribute("data-filter-type");
              telem.removeAttribute("data-filter");
            } else {
              telem.setAttribute("data-filter-type", copyStyle.dataset.filterType);
              telem.setAttribute("data-filter", copyStyle.dataset?.filter);
            }
          }
        } else {
          currentActiveElem.querySelector(".flippable").style.filter = sourceWidgetShadowProperty;
          currentActiveInnerElem.childNodes[0].style.filter = sourceWidgetImageFilterProperty;

          let telem = currentActiveInnerElem.childNodes[0];

          // remove or update data filter attribitues if only the source widget also have filter property
          if (
            filterInculdedWidgetType.includes(copyStyle.dataset["data-asset-type"]) ||
            (copyStyle.dataset["data-asset-type"] === UPLOAD && copyStyle.dataset["data-file-type"] !== SVG)
          ) {
            if (!copyStyle.dataset.filterType) {
              telem.removeAttribute("data-filter-type");
              telem.removeAttribute("data-filter");
            } else {
              telem.setAttribute("data-filter-type", copyStyle.dataset.filterType);
              telem.setAttribute("data-filter", copyStyle.dataset?.filter);
            }
          }
        }

        // set data attributre for flip, for all widget
        // currentActiveInnerElem.setAttribute("data-horizontal", copyStyle.dataset.horizontal ?? 1);
        // currentActiveInnerElem.setAttribute("data-vertical", copyStyle.dataset.vertical ?? 1);

        //Copy Border property
        if (
          borderInculdedWidgetType.includes(currentActiveWidget.current.dataset.assetType) &&
          borderInculdedWidgetType.includes(copyStyle.widgetType) &&
          !cropped &&
          !transparentImage
        ) {
          if (
            (currentActiveWidget.current.dataset.assetType === VIDEO &&
              currentActiveWidget.current.dataset.source === YOUTUBE) ||
            (currentActiveWidget.current.dataset.assetType === UPLOAD &&
              (currentActiveWidget.current.dataset.fileType === SVG ||
                currentActiveWidget.current.dataset.fileType === GIF)) ||
            (copyStyle.widgetType === VIDEO && copyStyle.dataset["data-source"] === YOUTUBE) ||
            (copyStyle.widgetType === UPLOAD &&
              (copyStyle.dataset["data-file-type"] === SVG || copyStyle.dataset["data-file-type"] === GIF))
          ) {
            console.log(
              "***** DO Nothing, Keep this console.log for now have to optimise the  condition later, as the condition is too big *****"
            );
          } else {
            let targetElem =
              currentActiveWidget.current.dataset.assetType === TEXT ||
              currentActiveWidget.current.dataset.assetType === QR_CODE ||
              currentActiveWidget.current.dataset.assetType === SHAPE ||
              currentActiveWidget.current.dataset.assetType === FRAME
                ? currentActiveInnerElem.style
                : [TYPE_FORM, JOT_FORM].includes(currentActiveWidget.current.dataset.assetType)
                ? currentActiveElem.querySelector(".customapp-frame").style
                : currentActiveElem.querySelector(".flippable").style;

            //Apply Border for Frame
            if (currentActiveWidget.current.dataset.assetType === FRAME) {
              const svg = currentActiveElem.querySelector("svg");
              const path = getNode(currentActiveWidgetId);
              const pathId = path.getAttribute("id");
              const clipPathId = svg.querySelector("clipPath").getAttribute("id");

              // remove border
              if (copyStyle.style.borderStyle === "none" || !copyStyle.style.borderStyle) {
                let borderUse;

                if (currentActiveElem.getAttribute("data-clipped") === "video") {
                  borderUse = currentActiveElem.querySelector(".svg-border-overlay");
                } else {
                  // Find the border <use> element (only direct children, not inside <clipPath>)
                  borderUse = Array.from(svg.children).find(
                    el =>
                      el.tagName === "use" &&
                      el.getAttributeNS("http://www.w3.org/1999/xlink", "href") === `#${pathId}` &&
                      !el.closest("clipPath")
                  );
                }

                // If found, remove it
                if (borderUse) {
                  borderUse.remove();
                }

                newDataAttr = {
                  "data-border-style": "none",
                  "data-border-width": 0,
                  "data-border-color": copyStyle.style?.borderColor,
                };
              } else {
                let existingUse;

                if (currentActiveElem.getAttribute("data-clipped") === "video") {
                  existingUse = currentActiveElem.querySelector(".svg-border-overlay");
                } else {
                  //Try to find the existing <use> by xlink:href
                  existingUse = Array.from(svg.querySelectorAll("use")).find(
                    use =>
                      use.getAttributeNS("http://www.w3.org/1999/xlink", "href") === `#${pathId}` &&
                      !use.closest("clipPath")
                  );
                }

                //If found, update stroke and stroke-width
                if (existingUse) {
                  existingUse.setAttribute("stroke", copyStyle.style.borderColor);
                  existingUse.setAttribute("stroke-width", copyStyle.style.borderWidth);
                }
                //Create a new <use> element in the SVG namespace
                else {
                  if (currentActiveElem.getAttribute("data-clipped") === "video") {
                    const clone = svg.cloneNode(true);
                    clone.classList.add("svg-border-overlay");
                    clone.querySelectorAll("image").forEach(img => img.remove());
                    clone.style.position = "absolute";
                    clone.style.left = "0px";
                    clone.style.top = "0px";
                    clone.style.overflow = "visible";
                    clone.setAttribute("fill", "none");
                    clone.setAttribute("stroke", copyStyle.style.borderColor);
                    clone.setAttribute("stroke-width", copyStyle.style.borderWidth);

                    currentActiveElem.appendChild(clone);
                  } else {
                    const useElement = document.createElementNS("http://www.w3.org/2000/svg", "use");

                    //Set required attributes
                    useElement.setAttributeNS("http://www.w3.org/1999/xlink", "xlink:href", `#${pathId}`);
                    useElement.setAttribute("fill", "none");
                    useElement.setAttribute("stroke", copyStyle.style.borderColor);
                    useElement.setAttribute("stroke-width", copyStyle.style.borderWidth);
                    useElement.setAttribute("clip-path", `url(#${clipPathId})`);

                    //Append it to the SVG
                    svg.appendChild(useElement);
                  }
                }

                newDataAttr = {
                  "data-border-style": "solid",
                  "data-border-width": copyStyle.style.borderWidth,
                  "data-border-color": copyStyle.style.borderColor,
                };
              }
            }
            //Apply Border for shape (Circles, Triangles, Basic)
            else if (
              currentActiveWidget.current.dataset.assetType === SHAPE &&
              ["Circles", "Triangles", "Basic"].includes(currentActiveWidget.current.dataset.category) &&
              parseInt(currentActiveWidget.current.dataset.version) >= 3
            ) {
              let pathId = getRandomString(8);
              let insidePathId = `inside_${getRandomString(8)}`;
              let node = getNode(currentActiveWidgetId);
              node.setAttribute("id", pathId);
              let newSvgNode = document.createElement("defs");

              // remove border
              if (copyStyle.style.borderStyle === "none" || !copyStyle.style.borderStyle) {
                newSvgNode.innerHTML = `${
                  node.outerHTML
                } <clipPath id=${insidePathId}><use xlink:href="#${pathId}"></use></clipPath> <use xlink:href="#${pathId}" stroke-width=${0} stroke="${
                  copyStyle.style.borderColor
                }" fill="none" clip-path="url(#${insidePathId})"></use>`;

                newDataAttr = {
                  "data-border-style": "none",
                  "data-border-width": 0,
                  "data-border-color": copyStyle.style.borderColor,
                };
              }
              // apply border
              else if (copyStyle.style.borderStyle) {
                newSvgNode.innerHTML = `${
                  node.outerHTML
                } <clipPath id=${insidePathId}><use xlink:href="#${pathId}"></use></clipPath> <use xlink:href="#${pathId}" stroke-width=${
                  parseInt(copyStyle.style.borderWidth) * 5
                } stroke="${copyStyle.style.borderColor}" fill="none" clip-path="url(#${insidePathId})"></use>`;
                newDataAttr = {
                  "data-border-style": "solid",
                  "data-border-width": copyStyle.style.borderWidth,
                  "data-border-color":
                    copyStyle.widgetType === SHAPE
                      ? copyStyle.style.borderColor
                      : RGBToHex(copyStyle.style.borderColor),
                };
              }

              if (copyStyle.style.borderStyle) currentActiveElem.querySelector(` svg`).innerHTML = newSvgNode.innerHTML;
            }
            //Apply Border for other widget and Rectangle Shapes
            else if (
              currentActiveWidget.current.dataset.assetType !== SHAPE ||
              (currentActiveWidget.current.dataset.assetType === SHAPE &&
                currentActiveWidget.current.dataset.category === "Rectangles" &&
                currentActiveWidget.current.dataset.version >= 3)
            ) {
              let border_style;
              if (
                currentActiveWidget.current.dataset.assetType === SHAPE &&
                currentActiveWidget.current.dataset.category === "Rectangles"
              ) {
                if (
                  copyStyle.style.borderStyle &&
                  copyStyle.style.borderStyle !== "none" &&
                  copyStyle.style.borderStyle !== "solid"
                )
                  border_style = "solid";
              }
              targetElem.borderStyle = border_style ? border_style : copyStyle.style.borderStyle;
              targetElem.borderWidth = `${parseInt(copyStyle.style.borderWidth)}px`;
              targetElem.borderRadius = copyStyle.style.borderRadius;
              targetElem.borderColor = copyStyle.style.borderColor;

              // fix shifting related issue when copy style border in any picture or video widget
              if (
                currentActiveWidget.current.dataset.assetType === PICTURE ||
                (currentActiveWidget.current.dataset.assetType === VIDEO &&
                  [STOCK, BRAND, UPLOAD_VIDEO].includes(currentActiveWidget.current.dataset.source)) ||
                (currentActiveWidget.current.dataset.assetType === UPLOAD &&
                  currentActiveWidget.current.dataset.fileType !== SVG)
              ) {
                if (copyStyle.style.borderStyle === "none" || !copyStyle.style.borderStyle) {
                  currentActiveElem.querySelector(".dhp-widget-inner").style.left = "";
                  currentActiveElem.querySelector(".dhp-widget-inner").style.top = "";
                  currentActiveElem.querySelector(".dhp-widget-inner").style.position = "";
                } else {
                  currentActiveElem.querySelector(".dhp-widget-inner").style.left = `-${parseInt(
                    copyStyle.style.borderWidth
                  )}px`;
                  currentActiveElem.querySelector(".dhp-widget-inner").style.top = `-${parseInt(
                    copyStyle.style.borderWidth
                  )}px`;
                  currentActiveElem.querySelector(".dhp-widget-inner").style.position = `relative`;
                }
              }
            }
          }
        }
      }

      // Copy color
      if (copyStyle.color.length > 0 && !colorExcludedWidgetType.includes(currentActiveElem.dataset.assetType)) {
        if (
          !copyStyle.dataset["data-grad-scolor"] &&
          (currentActiveElem.dataset.assetType === ILLUSTRATION ||
            currentActiveElem.dataset.assetType === STICKER ||
            currentActiveElem.dataset.assetType === ICON ||
            (currentActiveElem.dataset.assetType === TEXT_FRAME && currentActiveElem.dataset.version >= 3) ||
            (currentActiveElem.dataset.assetType === UPLOAD && currentActiveElem?.dataset?.fileType === SVG))
        ) {
          let colorClass = [];
          let newArray = [];

          currentActiveElem.querySelectorAll(` svg *`).forEach(function (node) {
            if (node.nodeName !== "g" && node.style && RGBToHex(node.style.fill) !== "") {
              let dataClassName = node.dataset.class;
              if (!colorClass.includes(dataClassName)) {
                colorClass.push(dataClassName);
              }
            }
          });

          colorClass.map((className, idx) => {
            newArray.push({ class: className, color: copyStyle.color[idx] });
          });

          newArray.map(value => {
            currentActiveElem.querySelectorAll(` svg *`).forEach(function (node) {
              if (value.class === node.dataset.class) {
                node.style.fill = value.color;
              }
            });
          });

          //Apply color for mono svg in upload widget
          if (
            colorClass.length === 0 &&
            (currentActiveElem.dataset.assetType === UPLOAD || currentActiveElem.dataset.assetType === ICON)
          ) {
            currentActiveElem.querySelector(` svg`).style.fill = copyStyle.color[0];
          }
        }
        //Apply color for text widget
        else if (currentActiveElem.dataset.assetType === TEXT) {
          //copy gradient color from shape to text
          if (copyStyle.dataset["data-widget"] === "SHAPE" && copyStyle.dataset["data-grad-scolor"]) {
            currentActiveInnerElem.style.backgroundImage = `linear-gradient(${copyStyle.dataset["data-grad-degree"]}deg, ${copyStyle.dataset["data-grad-scolor"]} ${copyStyle.dataset["data-grad-spercen"]}, ${copyStyle.dataset["data-grad-ecolor"]} ${copyStyle.dataset["data-grad-epercen"]})`;
            currentActiveInnerElem.style.webkitTextFillColor = "transparent";
            currentActiveInnerElem.style.backgroundColor = "transparent";
          } else {
            currentActiveInnerElem.style.color = copyStyle.color[0];

            // remove formatting from all child div and apply main formatting
            currentActiveInnerElem.querySelectorAll("*").forEach(node => {
              if (node.style?.color) {
                node.style.color = copyStyle.color[0];
              }
            });

            // remove formatting from all child div and apply main formatting if heading is applied
            if (changeHeading) {
              node.querySelectorAll("*").forEach(elm => {
                if (elm.style?.color) {
                  elm.style.color = copyStyle.color[0];
                }
              });
            }

            // Remove Gradient
            if (!copyStyle.dataset["data-grad-scolor"] && currentActiveElem.dataset.gradScolor) {
              removeGradColor = true;
              currentActiveInnerElem.style.backgroundImage = "";
              currentActiveInnerElem.style.webkitTextFillColor = "";

              if (changeHeading) {
                node.style.backgroundImage = "";
                node.style.webkitTextFillColor = "";
              }
            }
          }

          // copy data attribute
          if (copyStyle.dataset["data-grad-scolor"]) {
            if (currentActiveInnerElem.classList.contains("text-background-active")) {
              currentActiveInnerElem.classList.remove("text-background-active");
              if (changeHeading) node.classList.remove("text-background-active");
            }

            newDataAttr = {
              ...newDataAttr,
              "data-grad-degree": copyStyle.dataset["data-grad-degree"],
              "data-grad-scolor": copyStyle.dataset["data-grad-scolor"],
              "data-grad-spercen": copyStyle.dataset["data-grad-spercen"],
              "data-grad-ecolor": copyStyle.dataset["data-grad-ecolor"],
              "data-grad-epercen": copyStyle.dataset["data-grad-epercen"],
            };
          }
          // remove gradient data attribute
          else if (currentActiveElem.dataset.gradScolor) {
            if (
              copyStyle.style.backgroundColor &&
              copyStyle.style.backgroundColor !== "transparent" &&
              !currentActiveInnerElem.classList.contains("text-background-active")
            ) {
              currentActiveInnerElem.classList.add("text-background-active");
              if (changeHeading) node.classList.add("text-background-active");
            }

            let modifiedArray = JSON.parse(JSON.stringify(widgets));

            delete modifiedArray[currentActiveWidgetIdx].data["data-grad-degree"];
            delete modifiedArray[currentActiveWidgetIdx].data["data-grad-scolor"];
            delete modifiedArray[currentActiveWidgetIdx].data["data-grad-spercen"];
            delete modifiedArray[currentActiveWidgetIdx].data["data-grad-ecolor"];
            delete modifiedArray[currentActiveWidgetIdx].data["data-grad-epercen"];

            newDataAttr = modifiedArray[currentActiveWidgetIdx].data;
          } else {
            if (
              copyStyle.style.backgroundColor &&
              copyStyle.style.backgroundColor !== "transparent" &&
              !currentActiveInnerElem.classList.contains("text-background-active")
            ) {
              currentActiveInnerElem.classList.add("text-background-active");
              if (changeHeading) node.classList.add("text-background-active");
            } else if (
              copyStyle.style.backgroundColor === "transparent" &&
              currentActiveInnerElem.classList.contains("text-background-active")
            ) {
              currentActiveInnerElem.classList.remove("text-background-active");
              if (changeHeading) node.classList.remove("text-background-active");
            }
          }
        }
        //Apply gradient color for new shape selective category if copy widget has gradient color
        else if (
          copyStyle.dataset["data-grad-scolor"] &&
          currentActiveElem.dataset.assetType === SHAPE &&
          ["Basic", "Circles", "Triangles", "Rectangles"].includes(currentActiveElem.dataset.category) &&
          parseInt(currentActiveElem.dataset.version) >= 3
        ) {
          let newSvgNode = document.createElement("defs");
          let gradId = `grad_${getRandomString(8)}`;
          let node = document.querySelector(`#${currentActiveWidgetId} svg`);
          if (node.querySelector("linearGradient")) node.querySelector("linearGradient").remove();
          let svgPath = getNode(currentActiveWidgetId);
          svgPath.style.fill = `url(#${gradId})`;

          newSvgNode.innerHTML = `<linearGradient id="${gradId}" x1="0%" y1="0%" x2="100%" y2="0%" gradientTransform="rotate(${copyStyle.dataset["data-grad-degree"]})"><stop offset="${copyStyle.dataset["data-grad-spercen"]}" style="stop-color:${copyStyle.dataset["data-grad-scolor"]};stop-opacity:1"></stop><stop offset="${copyStyle.dataset["data-grad-epercen"]}" style="stop-color:${copyStyle.dataset["data-grad-ecolor"]};stop-opacity:1"></stop></linearGradient> ${node.innerHTML}`;

          currentActiveElem.querySelector(` svg`).innerHTML = newSvgNode.innerHTML;

          // copy data attribute
          if (copyStyle.dataset["data-grad-scolor"]) {
            newDataAttr = {
              ...newDataAttr,
              "data-grad-degree": copyStyle.dataset["data-grad-degree"],
              "data-grad-scolor": copyStyle.dataset["data-grad-scolor"],
              "data-grad-spercen": copyStyle.dataset["data-grad-spercen"],
              "data-grad-ecolor": copyStyle.dataset["data-grad-ecolor"],
              "data-grad-epercen": copyStyle.dataset["data-grad-epercen"],
            };
          }
        }
        //Apply color for QR Code
        else if (!copyStyle.dataset["data-grad-scolor"] && currentActiveElem.dataset.assetType === QR_CODE) {
          currentActiveElem.querySelectorAll(` svg *`).forEach(function (node, index) {
            if (node.nodeName !== "title") {
              node.style.fill = copyStyle.color[index];
            }
          });
          if (copyStyle.widgetType !== QR_CODE) {
            currentActiveInnerElem.style.backgroundColor = copyStyle.color[0]; // set margin area background if style is not copied from any qr-code widget
          }
        }
        // Apply color in Text art widget
        else if (currentActiveElem.dataset.assetType === TEXT_ART) {
          if (
            currentActiveElem.querySelector(".text-content-editor").getAttribute("data-transform-type") &&
            currentActiveElem.querySelector(".text-content-editor").getAttribute("data-transform-value")
          ) {
            currentActiveElem.querySelector(".text-content-editor svg path").setAttribute("fill", copyStyle.color[0]);
            currentActiveElem
              .querySelector(".text-content-editor")
              .setAttribute(
                "data-color-one",
                copyStyle.dataset["data-asset-type"] === TEXT ? RGBToHex(copyStyle.color[0]) : copyStyle.color[0]
              );
          }
          // path type text
          else if (
            currentActiveElem.querySelector(".text-content-editor").getAttribute("data-transform-type") &&
            currentActiveElem.querySelector(".text-content-editor").getAttribute("data-transform-type") !== "Path 1"
          ) {
            currentActiveElem.querySelector(".text-content-editor svg text").setAttribute("fill", copyStyle.color[0]);
            currentActiveElem
              .querySelector(".text-content-editor")
              .setAttribute(
                "data-color-one",
                copyStyle.dataset["data-asset-type"] === TEXT ? RGBToHex(copyStyle.color[0]) : copyStyle.color[0]
              );
          }
          // normal text
          else if (
            parseInt(currentActiveElem.querySelector(".text-content-editor").getAttribute("data-color-count")) === 1
          ) {
            currentActiveElem.querySelector(".text-content-editor").style.color = copyStyle.color[0];
            currentActiveElem
              .querySelector(".text-content-editor")
              .setAttribute(
                "data-color-one",
                copyStyle.dataset["data-asset-type"] === TEXT ? RGBToHex(copyStyle.color[0]) : copyStyle.color[0]
              );
          }
        }
        //Apply color for mono svg widget
        else if (
          !copyStyle.dataset["data-grad-scolor"] &&
          currentActiveElem.dataset.assetType !== UPLOAD &&
          currentActiveElem.querySelector(` svg`)
        ) {
          currentActiveElem.querySelector(` svg`).style.fill = copyStyle.color[0];

          // remove gradient color and data attribute
          if (currentActiveElem.dataset.gradScolor) {
            removeGradColor = true;
            let node = document.querySelector(`#${currentActiveWidgetId} svg`);
            if (node.querySelector("linearGradient")) node.querySelector("linearGradient").remove();
            let svgPath = getNode(currentActiveWidgetId);
            svgPath.style.fill = "";

            let modifiedArray = JSON.parse(JSON.stringify(widgets));

            delete modifiedArray[currentActiveWidgetIdx].data["data-grad-degree"];
            delete modifiedArray[currentActiveWidgetIdx].data["data-grad-scolor"];
            delete modifiedArray[currentActiveWidgetIdx].data["data-grad-spercen"];
            delete modifiedArray[currentActiveWidgetIdx].data["data-grad-ecolor"];
            delete modifiedArray[currentActiveWidgetIdx].data["data-grad-epercen"];

            newDataAttr = modifiedArray[currentActiveWidgetIdx].data;
          }
        }
      }

      //copy outline
      if (copyStyle.outline) {
        // Apply outline on text type widget
        if (currentActiveElem.dataset.assetType === TEXT) {
          //Remove outline property
          if (!copyStyle.outline.enable) {
            currentActiveElem.querySelector(".dhp-widget-inner").style.removeProperty("-webkit-text-stroke");
          }
          //Add outline property
          else {
            currentActiveElem.querySelector(
              ".dhp-widget-inner"
            ).style.webkitTextStroke = `${copyStyle.outline.width}px ${copyStyle.outline.color}`;
          }
        }
        // Apply outline on text art type widget
        else if (currentActiveElem.dataset.assetType === TEXT_ART) {
          //Add outline property
          if (copyStyle.outline.enable) {
            if (
              currentActiveElem.querySelector(".text-content-editor").getAttribute("data-transform-type") &&
              currentActiveElem.querySelector(".text-content-editor").getAttribute("data-transform-value")
            ) {
              currentActiveElem
                .querySelector(".text-content-editor svg path")
                .setAttribute("stroke-width", copyStyle.outline.width);
              currentActiveElem
                .querySelector(".text-content-editor svg path")
                .setAttribute("stroke", copyStyle.outline.color);
            }
            // path type text
            else if (
              currentActiveElem.querySelector(".text-content-editor").getAttribute("data-transform-type") &&
              currentActiveElem.querySelector(".text-content-editor").getAttribute("data-transform-type") !== "Path 1"
            ) {
              currentActiveElem
                .querySelector(".text-content-editor svg text")
                .setAttribute("stroke-width", copyStyle.outline.width);
              currentActiveElem
                .querySelector(".text-content-editor svg text")
                .setAttribute("stroke", copyStyle.outline.color);
            }
            // normal text
            else {
              currentActiveElem.querySelector(
                ".text-content-editor"
              ).style.webkitTextStroke = `${copyStyle.outline.width}px ${copyStyle.outline.color}`;
            }

            // set attributes
            currentActiveElem.querySelector(".text-content-editor").setAttribute("data-outline", "on");
            currentActiveElem
              .querySelector(".text-content-editor")
              .setAttribute("data-outline-width", copyStyle.outline.width);
            currentActiveElem
              .querySelector(".text-content-editor")
              .setAttribute("data-outline-color", copyStyle.outline.color);
          }
          //Remove outline property
          else {
            if (
              currentActiveElem.querySelector(".text-content-editor").getAttribute("data-transform-type") &&
              currentActiveElem.querySelector(".text-content-editor").getAttribute("data-transform-value")
            ) {
              currentActiveElem.querySelector(".text-content-editor svg path").removeAttribute("stroke-width");
              currentActiveElem.querySelector(".text-content-editor svg path").removeAttribute("stroke");
            }
            // path type text
            else if (
              currentActiveElem.querySelector(".text-content-editor").getAttribute("data-transform-type") &&
              currentActiveElem.querySelector(".text-content-editor").getAttribute("data-transform-type") !== "Path 1"
            ) {
              currentActiveElem.querySelector(".text-content-editor svg text").removeAttribute("stroke-width");
              currentActiveElem.querySelector(".text-content-editor svg text").removeAttribute("stroke");
            }
            // normal text
            else {
              currentActiveElem.querySelector(".text-content-editor").style.removeProperty("-webkit-text-stroke");
            }

            // remove attributes
            currentActiveElem.querySelector(".text-content-editor").removeAttribute("data-outline");
            currentActiveElem.querySelector(".text-content-editor").removeAttribute("data-outline-width");
            currentActiveElem.querySelector(".text-content-editor").removeAttribute("data-outline-color");
          }
        }
      }

      // Copy opacity
      currentActiveInnerElem.style.opacity = copyStyle.opacity;

      // Copy font family
      if (
        (currentActiveWidget.current.dataset.assetType === TEXT &&
          [PRICING_TABLE, TIMELINE, SCOPE_OF_WORK, ITINERARY, PORTFOLIO, CASESTUDY].includes(copyStyle.widgetType)) ||
        ([PRICING_TABLE, TIMELINE, SCOPE_OF_WORK, ITINERARY, PORTFOLIO, CASESTUDY].includes(
          currentActiveWidget.current.dataset.assetType
        ) &&
          copyStyle.widgetType === TEXT) ||
        ([PRICING_TABLE, TIMELINE, SCOPE_OF_WORK, ITINERARY, PORTFOLIO, CASESTUDY].includes(
          currentActiveWidget.current.dataset.assetType
        ) &&
          [PRICING_TABLE, TIMELINE, SCOPE_OF_WORK, ITINERARY, PORTFOLIO, CASESTUDY].includes(copyStyle.widgetType))
      ) {
        let currentFontFamily = copyStyle.style.fontFamily;

        // apply copy style if active widget is pricing table
        if (
          [PRICING_TABLE, TIMELINE, SCOPE_OF_WORK, ITINERARY, PORTFOLIO, CASESTUDY].includes(
            currentActiveWidget.current.dataset.assetType
          )
        ) {
          // check if font has bold
          const fonts = JSON.parse(localStorage?.getItem("allFonts"));
          let index = fonts.findIndex(
            font => font.name.toLowerCase() === currentFontFamily.replace(/["]+/g, "").toLowerCase()
          );

          // apply copy style if font has bold property
          if (fonts[index]?.bold_weight !== "") {
            currentActiveElem.querySelector(".pricingTable_editor").style.fontFamily = currentFontFamily;

            // update table config
            let tableConfig = currentActiveElem.getAttribute("data-table-config");
            tableConfig = JSON.parse(tableConfig);
            let newTableConfig = { ...tableConfig, "font-family": currentFontFamily };
            let jsonString = JSON.stringify(newTableConfig);

            // update new table config in data attr
            newDataAttr = {
              ...newDataAttr,
              "data-table-config": jsonString,
            };
          }
        }
        // apply copy style if active widget is text
        else currentActiveInnerElem.style.fontFamily = currentFontFamily;
      }

      if (
        [PRICING_TABLE, TIMELINE, SCOPE_OF_WORK, ITINERARY, PORTFOLIO, CASESTUDY].includes(
          currentActiveWidget.current.dataset.assetType
        )
      ) {
        newArray = Object.assign([...widgets], {
          [currentActiveWidgetIdx]: {
            ...widgets[currentActiveWidgetIdx],
            data: { ...widgets[currentActiveWidgetIdx].data, ...newDataAttr },
            innerHTML: currentActiveElem.innerHTML,
          },
        });
      } else if (!changeHeading) {
        let eightHandler =
          currentActiveWidget.current.dataset.assetType === PICTURE ||
          (currentActiveWidget.current.dataset.assetType === VIDEO &&
            [STOCK, BRAND, UPLOAD_VIDEO].includes(currentActiveWidget.current.dataset.source)) ||
          (currentActiveWidget.current.dataset.assetType === UPLOAD &&
            currentActiveWidget.current.dataset.fileType !== SVG);
        let TableWidget = currentActiveWidget.current.dataset.assetType === TABLE;
        let MapWidget = currentActiveWidget.current.dataset.assetType === MAP;

        newArray = Object.assign([...widgets], {
          [currentActiveWidgetIdx]: {
            ...widgets[currentActiveWidgetIdx],
            style: {
              ...widgets[currentActiveWidgetIdx].style,
              width:
                !currentActiveInnerElem.offsetWidth || eightHandler
                  ? widgets[currentActiveWidgetIdx].style.width
                  : TableWidget || MapWidget
                  ? window.getComputedStyle(currentActiveElem).width
                  : `${currentActiveInnerElem.offsetWidth}px`,
              height:
                !currentActiveInnerElem.offsetHeight || eightHandler
                  ? widgets[currentActiveWidgetIdx].style.height
                  : TableWidget || MapWidget
                  ? window.getComputedStyle(currentActiveElem).height
                  : `${currentActiveInnerElem.offsetHeight}px`,
            },
            data: removeGradColor
              ? { ...newDataAttr }
              : newDataAttr
              ? { ...widgets[currentActiveWidgetIdx].data, ...newDataAttr }
              : { ...widgets[currentActiveWidgetIdx].data },
            innerHTML: currentActiveElem.innerHTML,
          },
        });
      } else {
        newArray = Object.assign([...widgets], {
          [currentActiveWidgetIdx]: {
            ...widgets[currentActiveWidgetIdx],
            style: {
              ...widgets[currentActiveWidgetIdx].style,
              width: `${currentActiveInnerElem.offsetWidth}px`,
              height: `${currentActiveInnerElem.offsetHeight}px`,
            },
            data: removeGradColor
              ? { ...newDataAttr, "data-heading": headingValue }
              : newDataAttr
              ? { ...widgets[currentActiveWidgetIdx].data, ...newDataAttr, "data-heading": headingValue }
              : { ...widgets[currentActiveWidgetIdx].data, "data-heading": headingValue },
            innerHTML: !changeHeading ? currentActiveElem.innerHTML : node.outerHTML,
          },
        });
      }
      !changeLineThickness && updateWidgets(newArray);
      updateCopyStyle({ ...copyStyle, enable: false, lineThicknessFlag: changeLineThickness, changeList: changeList });
      setCopyStyleWidget(false);
    }
  }, [copyStyleWidget]);

  return setCopyStyleWidget;
};

export default useCopyStyleWidget;
